import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Commute } from '../types';
import { api } from '../utils/api';
import { processCommute, formatDuration, formatDateTime, filterCommutesByTime } from '../utils/commuteUtils';

// Fix for default markers in react-leaflet
import L from 'leaflet';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface CommuteMapProps {
  userId: number;
  selectedCommute: number | null;
  onCommuteSelect: (commuteId: number) => void;
  timeFilter: 'today' | 'week' | 'all';
  onTimeFilterChange: (timeFilter: 'today' | 'week' | 'all') => void;
}

const CommuteMap: React.FC<CommuteMapProps> = ({ userId, selectedCommute, onCommuteSelect, timeFilter, onTimeFilterChange }) => {
  const [commutes, setCommutes] = useState<Commute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getRandomColor = (index: number) => {
    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
      '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ];
    return colors[index % colors.length];
  };

  // Filter commutes based on time filter
  const filteredCommutes = filterCommutesByTime(commutes, timeFilter);

  const getMapCenter = () => {
    if (!Array.isArray(filteredCommutes) || filteredCommutes.length === 0) {
      return [47.6061, -122.3328]; // Default center (Seattle)
    }
    
    // Calculate average of all commute start points
    let totalLat = 0;
    let totalLon = 0;
    let validPoints = 0;
    
    filteredCommutes.forEach(commute => {
      if (commute.gps_points && commute.gps_points.length > 0) {
        totalLat += commute.gps_points[0].lat;
        totalLon += commute.gps_points[0].lon;
        validPoints++;
      }
    });
    
    if (validPoints === 0) {
      return [47.6061, -122.3328]; // Fallback to default
    }
    
    return [totalLat / validPoints, totalLon / validPoints];
  };

  const getMapZoom = () => {
    if (!Array.isArray(filteredCommutes) || filteredCommutes.length === 0) {
      return 13; // Default zoom
    }
    
    // Adjust zoom based on number of commutes and their spread
    if (filteredCommutes.length === 1) {
      return 14; // Closer zoom for single commute
    } else if (filteredCommutes.length <= 5) {
      return 12; // Medium zoom for few commutes
    } else {
      return 11; // Wider zoom for many commutes
    }
  };

  useEffect(() => {
    const fetchCommutes = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedCommutes = await api.getCommutes(userId);
        if (Array.isArray(fetchedCommutes)) {
          setCommutes(fetchedCommutes);
        } else {
          setCommutes([]);
          setError('Invalid response format from API');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch commutes');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchCommutes();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading map...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-96 flex items-center justify-center">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
      <div className="p-4 border-b">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Commute Map</h2>
            <p className="text-gray-600 text-sm mt-1">
              {Array.isArray(filteredCommutes) ? filteredCommutes.length : 0} commute{Array.isArray(filteredCommutes) && filteredCommutes.length !== 1 ? 's' : ''} found
              {timeFilter !== 'all' && (
                <span className="ml-2 text-blue-600">
                  ({timeFilter === 'today' ? 'today' : 'last week'})
                </span>
              )}
            </p>
          </div>
          
          {/* Time filter dropdown */}
          <div className="flex items-center gap-2">
            <label htmlFor="time-filter" className="text-sm text-gray-600 dark:text-gray-300">
              Show:
            </label>
            <select
              id="time-filter"
              value={timeFilter}
              onChange={(e) => {
                const newTimeFilter = e.target.value as 'today' | 'week' | 'all';
                onTimeFilterChange(newTimeFilter);
              }}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">Last Week</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
      </div>
      
      <div style={{ height: '600px', width: '100%' }}>
        <MapContainer
          center={getMapCenter() as [number, number]}
          zoom={getMapZoom()}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          
          {Array.isArray(filteredCommutes) && filteredCommutes.map((commute, index) => {
            const processedCommute = processCommute(commute);
            const color = getRandomColor(index);
            const isSelected = selectedCommute === commute.id;
            
            return (
              <React.Fragment key={commute.id}>
                <Polyline
                  key={`${commute.id}-${isSelected}`}
                  positions={commute.gps_points.map(point => [point.lat, point.lon])}
                  color={color}
                  weight={isSelected ? 6 : 4}
                  eventHandlers={{
                    click: () => onCommuteSelect(commute.id)
                  }}
                />
                
                {/* Start marker - first GPS coordinate */}
                {commute.gps_points.length > 0 && (
                  <Marker
                    position={[commute.gps_points[0].lat, commute.gps_points[0].lon]}
                    eventHandlers={{
                      click: () => onCommuteSelect(commute.id)
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">Start</p>
                        <p>{formatDateTime(processedCommute.startDate)}</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* End marker - last GPS coordinate */}
                {commute.gps_points.length > 1 && (
                  <Marker
                    position={[
                      commute.gps_points[commute.gps_points.length - 1].lat,
                      commute.gps_points[commute.gps_points.length - 1].lon
                    ]}
                    eventHandlers={{
                      click: () => onCommuteSelect(commute.id)
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">End</p>
                        <p>{formatDateTime(processedCommute.endDate)}</p>
                        <p>Duration: {formatDuration(processedCommute.duration)}</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
      
      {selectedCommute && (
        <div className="p-4 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            Selected commute: {selectedCommute}
          </p>
        </div>
      )}
    </div>
  );
};

export default CommuteMap;
